import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Loader2, Calendar, Plus, ChevronRight, UserPlus } from 'lucide-react'
import type { Season } from './types'
import { formatDate, getStatusDot, getStatusLabel } from './utils'

interface SeasonsListViewProps {
  seasons: Season[]
  seasonsLoading: boolean
  getBracketCount: (seasonId: string) => number
  onSeasonClick: (seasonId: string) => void
  onNewSeason: () => void
  actionsVisible: boolean
}

export function SeasonsListView({
  seasons,
  seasonsLoading,
  getBracketCount,
  onSeasonClick,
  onNewSeason,
  actionsVisible,
}: SeasonsListViewProps) {
  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Seasons
            </CardTitle>
            <CardDescription>Select a season to manage its brackets</CardDescription>
          </div>
          <Button 
            size="sm" 
            onClick={onNewSeason}
            className={`transition-all duration-300 ease-out ${
              actionsVisible ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0'
            }`}
          >
            <Plus className="h-4 w-4 mr-1" />
            New Season
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {seasonsLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : seasons.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No seasons yet. Create one to get started.
          </div>
        ) : (
          <div className="space-y-2">
            {seasons.map(season => {
              const bracketCount = getBracketCount(season.seasonId)
              return (
                <div
                  key={season.seasonId}
                  className="flex items-center gap-2 sm:gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer group"
                  onClick={() => onSeasonClick(season.seasonId)}
                >
                  <div className={`h-2.5 w-2.5 rounded-full shrink-0 ${getStatusDot(season.status)}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{season.name}</span>
                      <span className="text-xs text-muted-foreground">{getStatusLabel(season.status)}</span>
                      {season.status === 'upcoming' && season.signupsOpen && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 flex items-center gap-1">
                          <UserPlus className="h-3 w-3" />
                          <span className="hidden sm:inline">Signups Open</span>
                        </span>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {formatDate(season.startDate)} – {formatDate(season.endDate)}
                      {bracketCount > 0 && <span className="ml-2">• {bracketCount} bracket{bracketCount !== 1 ? 's' : ''}</span>}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
